import React, { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { Play, Pause } from "lucide-react";
import { toast } from "sonner";

const AudioPlayer = ({ slug, language = "en", voice }) => {
    const [playing, setPlaying] = useState(false);
    const [loading, setLoading] = useState(false);
    const [speed, setSpeed] = useState(1.0);
    const [error, setError] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const audioRef = useRef(null);

    // Reset audio whenever the prayer or language changes so switching EN→ES→LA
    // fetches a fresh recording (was previously replaying the first language).
    useEffect(() => {
        setPlaying(false);
        setError(false);
        setAudioUrl(prev => {
            if (prev) {
                try { URL.revokeObjectURL(prev); } catch { /* ignore */ }
            }
            return null;
        });
        if (audioRef.current) {
            try { audioRef.current.pause(); } catch { /* ignore */ }
        }
    }, [slug, language]);

    // Cleanup any remaining blob URL on true unmount only.
    const urlRef = useRef(null);
    useEffect(() => { urlRef.current = audioUrl; }, [audioUrl]);
    useEffect(() => () => {
        if (urlRef.current) {
            try { URL.revokeObjectURL(urlRef.current); } catch { /* ignore */ }
        }
    }, []);

    const load = async () => {
        setLoading(true);
        setError(false);
        try {
            const res = await api.post("/tts/prayer", { prayer_slug: slug, language }, { responseType: "blob" });
            // Guard: reject anything that isn't audio (defensive against upstream error surfaces)
            if (!res.data || (res.data.type && !res.data.type.startsWith("audio/"))) {
                throw new Error("not-audio");
            }
            const url = URL.createObjectURL(res.data);
            setAudioUrl(url);
            return url;
        } catch (e) {
            setError(true);
            toast.error("Voice is resting", { description: "Please try again in a moment." });
            return null;
        } finally { setLoading(false); }
    };

    const toggle = async () => {
        try {
            let url = audioUrl;
            if (!url) url = await load();
            if (!url || !audioRef.current) return;
            if (playing) {
                audioRef.current.pause();
                setPlaying(false);
            } else {
                audioRef.current.playbackRate = speed;
                // Wrap play() promise — browsers reject it under autoplay policy / aborts
                const p = audioRef.current.play();
                if (p && typeof p.catch === "function") {
                    await p.catch((err) => {
                        setPlaying(false);
                        if (err && err.name !== "AbortError") {
                            toast.error("Could not start playback", { description: "Please tap the play button again." });
                        }
                    });
                }
                setPlaying(true);
            }
        } catch (err) {
            setPlaying(false);
            setError(true);
            toast.error("Playback interrupted", { description: "Please try again." });
        }
    };

    return (
        <div className="flex items-center gap-4 px-5 py-4 border border-border missal-card" data-testid="audio-player">
            <button onClick={toggle} data-testid="audio-play-btn" disabled={loading}
                className="w-12 h-12 rounded-full bg-liturgy text-white flex items-center justify-center hover:scale-105 transition-transform duration-500 disabled:opacity-40">
                {loading ? <span className="text-xs">…</span> : playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
            </button>
            <div className="flex-1">
                <div className="small-caps text-[11px] text-muted-foreground">{voice === 'female' ? 'Female voice' : 'Male voice'} · {language.toUpperCase()}</div>
                <div className="text-sm mt-1">{error ? 'Voice is resting. Please try again shortly.' : 'Prayed deeply, from the heart — at the pace of a rosary bead.'}</div>
            </div>
            <div className="flex items-center gap-1 text-xs">
                {[0.75, 1.0, 1.25].map(s => (
                    <button key={s} onClick={() => { setSpeed(s); if (audioRef.current) audioRef.current.playbackRate = s; }} data-testid={`speed-${s}`}
                        className={`px-2 py-1 border ${speed===s ? 'border-liturgy accent-liturgy' : 'border-transparent text-muted-foreground'}`}>
                        {s}×
                    </button>
                ))}
            </div>
            <audio ref={audioRef} src={audioUrl || undefined}
                onEnded={() => setPlaying(false)}
                onError={() => { setPlaying(false); setError(true); }}
            />
        </div>
    );
};

export default AudioPlayer;
